import _, {isUndefined} from 'lodash';
import React, {FC, useEffect} from 'react';
import {connect} from 'react-redux';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {AppState} from '../../../../store/appState';
import {getCards} from '../../../../store/data/dataSelectors';
import {
  closeAccountDetailsAction,
  fetchCardsForAccountDetailsAction,
} from '../../../../store/panels/accounts/accountDetailsPanelActions';
import {AccountDetailsCardsState} from '../../../../store/panels/accounts/accountDetailsPanelState';
import {ContactOrAccountDetailsPanelId} from '../../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {AccountDetailsContacts} from './accountDetailsContacts';

/*
 * Props.
 */

interface AccountDetailsContactsContainerPublicProps {
  panelId: ContactOrAccountDetailsPanelId;
  account: AccountModel;
  cardsState: AccountDetailsCardsState | undefined;
}
type AccountDetailsContactsContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountDetailsContactsContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountDetailsContactsContainerProps = AccountDetailsContactsContainerPublicProps &
  AccountDetailsContactsContainerStateProps &
  AccountDetailsContactsContainerDispatchProps;

/*
 * Selectors.
 */

function getCardsState(_state: AppState, props: AccountDetailsContactsContainerPublicProps) {
  return props.cardsState;
}

function getAccount(_state: AppState, props: AccountDetailsContactsContainerPublicProps) {
  return props.account;
}

function makeGetCardsStateCards() {
  return createSelector([getCardsState, getCards, getAccount], (state, cards, account) => {
    if (!state || state.loadingStatus !== AsyncStatusesEnum.LOADED || isUndefined(state.cardIds)) {
      return undefined;
    }
    return _(state.cardIds)
      .map((cardId) => cards[cardId])
      .filter((card) => card?.accountId === account.id)
      .compact()
      .value();
  });
}

/*
 * Component.
 */

const AccountDetailsContactsContainerComponent: FC<AccountDetailsContactsContainerProps> = (props) => {
  const {panelId, account, cards, cardsState, fetchCardsForAccountDetails, closeSidePanel, navigateToLink} =
    props;

  useEffect(() => {
    if (!cardsState) {
      fetchCardsForAccountDetails(panelId, account.id);
    }
  }, [panelId, account.id, cardsState, fetchCardsForAccountDetails]);

  if (!cardsState) {
    return null;
  }
  return (
    <AccountDetailsContacts
      panelId={panelId}
      account={account}
      cardsState={cardsState}
      cards={cards}
      closeSidePanel={closeSidePanel}
      navigateToLink={navigateToLink}
      fetchCardsForAccountDetails={fetchCardsForAccountDetails}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountDetailsContactsContainerPublicProps>()(() => ({
  cardsState: getCardsState,
  cards: makeGetCardsStateCards(),
}));

const mapDispatchToProps = makeMapDispatchToProps<AccountDetailsContactsContainerPublicProps>()((props) => ({
  fetchCardsForAccountDetails: fetchCardsForAccountDetailsAction,
  closeSidePanel: () => closeAccountDetailsAction(props.panelId, true),
  navigateToLink: navigateToLinkAction,
}));

export const AccountDetailsContactsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountDetailsContactsContainerComponent);
